import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Truck, Code2, LayoutGrid } from "lucide-react";
import { C } from "./ui";
import { Shield, Wordmark, Serif, Label } from "../consumer/ui";

const SCREENS = [["consumer", "ResQ1 · Consumer"], ["commercial", "ResQconnect · Commercial"]];

export default function MarketingWireframe() {
  const [screen, setScreen] = useState("consumer");
  return (
    <div style={{ minHeight: "100vh", background: C.body, fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="marketing-wireframe">
      <header style={{ position: "sticky", top: 0, zIndex: 20, display: "flex", alignItems: "center", gap: 16, padding: "14px 32px", borderBottom: "1px solid rgba(26,30,36,0.08)", background: "rgba(247,245,240,0.92)", backdropFilter: "blur(10px)" }}>
        <Wordmark />
        <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: C.slate }}>WIREFRAME · MARKETING</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 5, background: "#fff", padding: 4, borderRadius: 999, border: "1px solid rgba(26,30,36,0.1)" }}>
          {SCREENS.map(([id, label]) => (
            <button key={id} onClick={() => setScreen(id)} data-testid={`mkt-tab-${id}`}
              style={{ padding: "6px 14px", borderRadius: 999, border: 0, background: screen === id ? C.ink : "transparent", color: screen === id ? "#fff" : C.slate, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>{label}</button>
          ))}
        </div>
        <Link to="/prototype" data-testid="mkt-to-hub" style={{ display: "flex", alignItems: "center", gap: 6, color: C.slate, fontSize: 12.5, textDecoration: "none" }}><LayoutGrid size={15} /> Hub</Link>
      </header>

      {screen === "consumer" ? <Consumer /> : <Commercial />}
    </div>
  );
}

function Consumer() {
  return (
    <section style={{ maxWidth: 1080, margin: "0 auto", padding: "70px 32px" }} data-testid="mkt-consumer">
      <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 50, alignItems: "center" }}>
        <div>
          <Label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}><span style={{ width: 24, height: 1, background: C.red }} /> Family emergency protection</Label>
          <Serif size={58} style={{ lineHeight: 1.05 }}>Protection, <br /><em style={{ color: C.slate }}>one tap</em> away.</Serif>
          <p style={{ fontSize: 18, color: C.slate, lineHeight: 1.6, margin: "24px 0 32px", maxWidth: 460 }}>
            One button connects your whole household to a 24/7 control room — with your location, medical details and armed-response partner already in hand.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button data-testid="mkt-consumer-cta" style={{ padding: "14px 26px", background: C.ink, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>Download the app <ArrowRight size={17} /></button>
            <button style={{ padding: "14px 26px", background: "transparent", color: C.ink, border: `1.5px solid ${C.ink}`, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: "pointer" }}>How it works</button>
          </div>
          <div style={{ display: "flex", gap: 24, marginTop: 40 }}>
            {[["8 yrs", "flawless track record"], ["24/7", "control room"], ["4 min", "avg response"]].map(([a, b]) => (
              <div key={a}><div style={{ fontFamily: "'Fraunces', serif", fontSize: 28, fontWeight: 600, color: C.red }}>{a}</div><div style={{ fontSize: 12, color: C.slate }}>{b}</div></div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ width: 260, background: "#0a0d12", borderRadius: 36, padding: 12, boxShadow: "0 30px 80px rgba(0,0,0,0.3)" }}>
            <div style={{ background: C.body, borderRadius: 26, padding: "40px 22px", textAlign: "center", minHeight: 420, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20 }}>
              <Shield size={48} />
              <div style={{ width: 150, height: 150, borderRadius: "50%", background: C.red, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, letterSpacing: "0.05em", boxShadow: `0 12px 40px ${C.red}66` }}>HOLD<br />TO PANIC</div>
              <div style={{ fontSize: 12, color: C.slate }}>System active · GPS locked</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Commercial() {
  return (
    <section style={{ maxWidth: 1080, margin: "0 auto", padding: "70px 32px" }} data-testid="mkt-commercial">
      <Label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18 }}><span style={{ width: 24, height: 1, background: C.navy }} /> For fleets, insurers &amp; platforms</Label>
      <Serif size={54} style={{ lineHeight: 1.06, maxWidth: 760 }}>Emergency dispatch, <em style={{ color: C.slate }}>as infrastructure.</em></Serif>
      <p style={{ fontSize: 18, color: C.slate, lineHeight: 1.6, margin: "22px 0 40px", maxWidth: 620 }}>
        ResQconnect Commercial puts our control room behind your brand — via a managed fleet portal or a clean API your product calls directly.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 44 }}>
        {[
          { icon: Truck, title: "Fleet portal", pts: ["Driver & vehicle roster", "Live incident map", "SLA & response reporting"] },
          { icon: Code2, title: "Dispatch API", pts: ["REST endpoints + webhooks", "White-label incident flow", "Sandbox with mock dispatch"] },
        ].map((card) => {
          const Icon = card.icon;
          return (
            <div key={card.title} style={{ background: "#fff", border: "1px solid rgba(26,30,36,0.08)", borderRadius: 16, padding: 28 }} data-testid={`mkt-card-${card.title.split(" ")[0].toLowerCase()}`}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: `${C.navy}0f`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}><Icon size={22} color={C.navy} /></div>
              <Serif size={24}>{card.title}</Serif>
              <div style={{ marginTop: 14, display: "grid", gap: 9 }}>
                {card.pts.map((p) => <div key={p} style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 14, color: C.slate }}><Check size={16} color={C.green} /> {p}</div>)}
              </div>
            </div>
          );
        })}
      </div>
      <button data-testid="mkt-commercial-cta" style={{ padding: "14px 26px", background: C.navy, color: "#fff", border: 0, borderRadius: 999, fontSize: 15, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>Book a partner demo <ArrowRight size={17} /></button>
    </section>
  );
}
